
import React, { useEffect, useState } from "react";
import Layout from "../core/Layout";
import { useListSettings, useUpdateSetting } from "./api/setting";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import showToast from "../utils/toast";

const styles = {
	container: {
		padding: "30px",
		fontFamily: "'Segoe UI', 'Roboto', 'Arial', sans-serif",
		maxWidth: "900px",
		margin: "30px auto",
		backgroundColor: "#ffffff",
		border: "none",
		borderRadius: "16px",
		boxShadow: "0 8px 32px rgba(102, 126, 234, 0.15)",
		background: "linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)",
	},
	heading: {
		textAlign: "center",
		color: "#4a5568",
		marginBottom: "30px",
		fontSize: "28px",
		fontWeight: "600",
		background: "linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)",
		WebkitBackgroundClip: "text",
		WebkitTextFillColor: "transparent",
		backgroundClip: "text",
	},
	addTreatmentSection: {
		display: "flex",
		gap: "15px",
		marginBottom: "30px",
		padding: "25px",
		backgroundColor: "#ffffff",
		borderRadius: "12px",
		border: "2px solid #e2e8f0",
		boxShadow: "0 2px 8px rgba(102, 126, 234, 0.08)",
	},
	input: {
		flex: 1,
		padding: "12px 16px",
		border: "2px solid #e2e8f0",
		borderRadius: "8px",
		fontSize: "14px",
		transition: "all 0.3s ease",
		backgroundColor: "#fafbfc",
		color: "#4a5568",
		outline: "none",
	},
	inputFocus: {
		borderColor: "rgb(102, 126, 234)",
		backgroundColor: "#ffffff",
		boxShadow: "0 0 0 3px rgba(102, 126, 234, 0.1)",
	},
	button: {
		padding: "12px 24px",
		margin: "0px 4px",
		background: "linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)",
		color: "white",
		border: "none",
		borderRadius: "8px",
		cursor: "pointer",
		fontSize: "14px",
		fontWeight: "500",
		transition: "all 0.3s ease",
		boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
	},
	buttonHover: {
		transform: "translateY(-2px)",
		boxShadow: "0 4px 16px rgba(102, 126, 234, 0.4)",
	},
	buttonSecondary: {
		background: "linear-gradient(135deg, #64748b 0%, #475569 100%)",
		boxShadow: "0 2px 8px rgba(100, 116, 139, 0.3)",
	},
	buttonDanger: {
		background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
		boxShadow: "0 2px 8px rgba(239, 68, 68, 0.3)",
	},
	list: {
		listStyleType: "none",
		padding: 0,
		margin: 0,
	},
	listItem: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: "20px 25px",
		marginBottom: "12px",
		backgroundColor: "#ffffff",
		borderRadius: "12px",
		border: "2px solid #e2e8f0",
		transition: "all 0.3s ease",
		boxShadow: "0 2px 4px rgba(102, 126, 234, 0.05)",
	},
	listItemHover: {
		borderColor: "rgb(102, 126, 234)",
		boxShadow: "0 4px 16px rgba(102, 126, 234, 0.1)",
		transform: "translateY(-2px)",
	},
	editingInput: {
		flex: 1,
		padding: "12px 16px",
		marginRight: "15px",
		border: "2px solid #e2e8f0",
		borderRadius: "8px",
		fontSize: "14px",
		backgroundColor: "#fafbfc",
		color: "#4a5568",
		outline: "none",
		transition: "all 0.3s ease",
	},
	treatmentText: {
		fontSize: "16px",
		color: "#2d3748",
		fontWeight: "500",
	},
	treatmentName: {
		color: "#4a5568",
		fontWeight: "600",
	},
	treatmentPrice: {
		color: "rgb(102, 126, 234)",
		fontWeight: "700",
	},
	emptyState: {
		textAlign: "center",
		padding: "40px",
		color: "#718096",
		fontSize: "16px",
		fontStyle: "italic",
	},
};

const TreatmentList = () => {
	const [treatments, setTreatments] = useState([]);
	const [newTreatment, setNewTreatment] = useState({ name: "", price: "" });
	const [editingIndex, setEditingIndex] = useState(null);
	const [editingTreatment, setEditingTreatment] = useState({
		name: "",
		price: "",
	});

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const updateSettingMutation = useUpdateSetting();

	const addTreatment = () => {
		if (newTreatment.name.trim() && newTreatment.price.trim()) {
			setTreatments([...treatments, { ...newTreatment }]);
			setNewTreatment({ name: "", price: "" });
		} else {
			showToast("Please fill in both name and price");
		}
	};

	const deleteTreatment = (index) => {
		setTreatments(treatments.filter((_, i) => i !== index));
	};

	const userLogin = useSelector((state) => state.userLogin);
	const { userInfo } = userLogin;

	const { data: settings } = useListSettings();

	useEffect(() => {
		if (treatments.length > 0) {
			updateSettingMutation.mutate({ category: null, setting: { treatment: treatments } });
		}
	}, [treatments]);

	useEffect(() => {
		if (!userInfo) {
			navigate("/signin");
		}
	}, [userInfo, navigate]);

	const startEditing = (index) => {
		setEditingIndex(index);
		setEditingTreatment(treatments[index]);
	};

	const saveEditing = () => {
		if (editingTreatment.name.trim() && editingTreatment.price.trim()) {
			setTreatments(
				treatments.map((treatment, i) =>
					i === editingIndex ? { ...editingTreatment } : treatment
				)
			);
			setEditingIndex(null);
			setEditingTreatment({ name: "", price: "" });
		} else {
			showToast("Please provide both name and price");
		}
	};

	useEffect(() => {
		if (settings?.data.treatment) {
			setTreatments(settings.data.treatment);
		}
	}, [settings]);

	return (
		<Layout title={"Treatment List"}>
			<div style={styles.container}>
				<h3 style={styles.heading}>Treatment List</h3>
				<div style={styles.addTreatmentSection}>
					<input
						style={styles.input}
						type="text"
						placeholder="Treatment name"
						value={newTreatment.name}
						onChange={(e) =>
							setNewTreatment({
								...newTreatment,
								name: e.target.value,
							})
						}
						onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
						onBlur={(e) => {
							e.target.style.borderColor = "#e2e8f0";
							e.target.style.backgroundColor = "#fafbfc";
							e.target.style.boxShadow = "none";
						}}
					/>
					<input
						style={styles.input}
						type="number"
						placeholder="Price"
						value={newTreatment.price}
						onChange={(e) =>
							setNewTreatment({
								...newTreatment,
								price: e.target.value,
							})
						}
						onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
						onBlur={(e) => {
							e.target.style.borderColor = "#e2e8f0";
							e.target.style.backgroundColor = "#fafbfc";
							e.target.style.boxShadow = "none";
						}}
					/>
					<button 
						style={styles.button} 
						onClick={addTreatment}
						onMouseEnter={(e) => Object.assign(e.target.style, styles.buttonHover)}
						onMouseLeave={(e) => {
							e.target.style.transform = "translateY(0)";
							e.target.style.boxShadow = "0 2px 8px rgba(102, 126, 234, 0.3)";
						}}
					>
						Add
					</button>
				</div>

				{treatments.length === 0 ? (
					<div style={styles.emptyState}>
						Add a treatment to get started
					</div>
				) : (
					<ul style={styles.list}>
						{treatments.map((treatment, index) => (
							<li key={index} style={styles.listItem}>
								{editingIndex === index ? (
									<>
										<input
											style={styles.editingInput}
											type="text"
											placeholder="Name"
											value={editingTreatment.name}
											onChange={(e) =>
												setEditingTreatment({
													...editingTreatment,
													name: e.target.value,
												})
											}
											onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
											onBlur={(e) => {
												e.target.style.borderColor = "#e2e8f0";
												e.target.style.backgroundColor = "#fafbfc";
												e.target.style.boxShadow = "none";
											}}
										/>
										<input
											style={styles.editingInput}
											type="number"
											placeholder="Price"
											value={editingTreatment.price}
											onChange={(e) =>
												setEditingTreatment({
													...editingTreatment,
													price: e.target.value,
												})
											}
											onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
											onBlur={(e) => {
												e.target.style.borderColor = "#e2e8f0";
												e.target.style.backgroundColor = "#fafbfc";
												e.target.style.boxShadow = "none";
											}}
										/>
										<button
											style={styles.button}
											onClick={saveEditing}
											onMouseEnter={(e) => Object.assign(e.target.style, styles.buttonHover)}
											onMouseLeave={(e) => {
												e.target.style.transform = "translateY(0)";
												e.target.style.boxShadow = "0 2px 8px rgba(102, 126, 234, 0.3)";
											}}
										>
											Save
										</button>
										<button
											style={{
												...styles.button,
												...styles.buttonSecondary,
											}}
											onClick={() =>
												setEditingIndex(null)
											}
											onMouseEnter={(e) => Object.assign(e.target.style, styles.buttonHover)}
											onMouseLeave={(e) => {
												e.target.style.transform = "translateY(0)";
												e.target.style.boxShadow = "0 2px 8px rgba(100, 116, 139, 0.3)";
											}}
										>
											Cancel
										</button>
									</>
								) : (
									<>
										<span style={styles.treatmentText}>
											<span style={styles.treatmentName}>{treatment.name}</span> -{" "}
											<span style={styles.treatmentPrice}>₹{treatment.price}</span>
										</span>
										<div>
											<button
												style={styles.button}
												onClick={() =>
													startEditing(index)
												}
												onMouseEnter={(e) => Object.assign(e.target.style, styles.buttonHover)}
												onMouseLeave={(e) => {
													e.target.style.transform = "translateY(0)";
													e.target.style.boxShadow = "0 2px 8px rgba(102, 126, 234, 0.3)";
												}}
											>
												Edit
											</button>
											<button
												style={{
													...styles.button,
													...styles.buttonDanger,
												}}
												onClick={() =>
													deleteTreatment(index)
												}
												onMouseEnter={(e) => Object.assign(e.target.style, styles.buttonHover)}
												onMouseLeave={(e) => {
													e.target.style.transform = "translateY(0)";
													e.target.style.boxShadow = "0 2px 8px rgba(239, 68, 68, 0.3)";
												}}
											>
												Delete
											</button>
										</div>
									</>
								)}
							</li>
						))}
					</ul>
				)}
			</div>
		</Layout>
	);
};

export default TreatmentList;